/**
 * Moments Service - API calls for moments management
 * Portfolio visuel des realisations professionnelles
 */


const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';
const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le Moment (ou sa photo) visé appartient
 * bien à l'appelant. Il lui faut donc une identité prouvée : le JWT signé au
 * login, et non le `peopleId` que le corps de la requête déclarait jusqu'ici
 * — ce dernier venait du client, et se remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Get all moments for a user
 * @param {number} peopleId - User ID
 * @param {Object} filters - Optional filters (occupationId, includeInactive)
 * @returns {Promise<Array>} List of moments with photos and skills
 */
export const getMomentsList = async (peopleId, filters = {}) => {
    try {
        let url = `${API_BASE}/moments?peopleId=${peopleId}`;

        if (filters.occupationId) {
            url += `&occupationId=${filters.occupationId}`;
        }
        if (filters.includeInactive) {
            url += `&includeInactive=true`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch moments');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching moments:', error);
        return [];
    }
};

/**
 * Get moments for public profile
 * @param {string} peopleUuid - User UUID
 * @returns {Promise<Array>} List of active moments
 */
export const getProfileMoments = async (peopleUuid) => {
    try {
        const response = await fetch(`${API_BASE}/moments/profile/${peopleUuid}`);
        if (!response.ok) throw new Error('Failed to fetch profile moments');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching profile moments:', error);
        return [];
    }
};

/**
 * Get a specific moment by UUID
 * @param {string} uuid - Moment UUID
 * @returns {Promise<Object|null>} Moment with photos and skills, or null
 */
export const getMomentByUuid = async (uuid) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${uuid}`);
        if (!response.ok) throw new Error('Failed to fetch moment');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        return success ? data : null;
    } catch (error) {
        console.error('Error fetching moment:', error);
        return null;
    }
};

/**
 * Create a new moment
 * @param {Object} data - Moment data
 * @param {number} data.peopleId - User ID
 * @param {string} data.title - Title
 * @param {string} data.description - Description (optional)
 * @param {number} data.occupationId - Occupation ID (optional)
 * @returns {Promise<Object>} Result with success status, id, uuid
 */
export const createMoment = async (data) => {
    try {
        const response = await fetch(`${API_BASE}/moments`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(data)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            uuid: result.uuid ?? result.UUID ?? '',
            message: result.message ?? result.MESSAGE ?? '',
            denyCode: result.denyCode ?? result.DENYCODE,
            current: result.current ?? result.CURRENT,
            max: result.max ?? result.MAX,
            moduleCode: result.moduleCode ?? result.MODULECODE
        };
    } catch (error) {
        console.error('Error creating moment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a moment
 * @param {number} momentId - Moment ID
 * @param {Object} data - Update data (title, description, occupationId, isActive)
 * @returns {Promise<Object>} Result with success status
 */
export const updateMoment = async (momentId, data) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(data)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error updating moment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a moment
 * @param {number} momentId - Moment ID
 * @returns {Promise<Object>} Result with success status and photos to delete
 */
export const deleteMoment = async (momentId) => {
    try {
        const url = `${API_BASE}/moments/${momentId}`;

        const response = await fetch(url, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? '',
            photos: result.photos ?? result.PHOTOS ?? []
        };
    } catch (error) {
        console.error('Error deleting moment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Toggle moment active status
 * @param {number} momentId - Moment ID
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Result with success status
 */
export const toggleMomentActive = async (momentId, isActive) => {
    return updateMoment(momentId, { isActive });
};

// ============================================
// PHOTOS
// ============================================

/**
 * Get photos for a moment
 * @param {number} momentId - Moment ID
 * @returns {Promise<Array>} List of photos
 */
export const getMomentPhotos = async (momentId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/photos`);
        if (!response.ok) throw new Error('Failed to fetch photos');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching moment photos:', error);
        return [];
    }
};

/**
 * Upload and add a photo to a moment (single atomic call)
 * @param {number} momentId - Moment ID
 * @param {string} momentUuid - Moment UUID (unused, kept for API compat)
 * @param {File} file - Image file
 * @param {number} displayOrder - Display order (unused, computed server-side)
 * @param {string} caption - Caption (optional)
 * @param {string} peopleUuid - People UUID (unused, computed server-side)
 * @returns {Promise<Object>} Result with success status
 */
export const uploadMomentPhoto = async (momentId, momentUuid, file, displayOrder = 1, caption = '', peopleUuid = '') => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (caption) formData.append('caption', caption);

        const response = await fetch(`${API_BASE}/moments/${momentId}/photos/upload`, {
            method: 'POST',
            headers: enTetesEcriture(false),
            body: formData
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? '',
            imagePath: result.imagePath ?? result.IMAGEPATH ?? ''
        };
    } catch (error) {
        console.error('Error uploading moment photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Add a photo reference to a moment (when file is already uploaded)
 * @param {number} momentId - Moment ID
 * @param {string} imagePath - Path to the uploaded image
 * @param {number} displayOrder - Display order
 * @param {string} caption - Caption (optional)
 * @returns {Promise<Object>} Result with success status
 */
export const addMomentPhoto = async (momentId, imagePath, displayOrder = 1, caption = '') => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/photos`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ imagePath, displayOrder, caption })
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding moment photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a photo from a moment
 * @param {number} photoId - Photo ID
 * @returns {Promise<Object>} Result with success status and imagePath for cleanup
 */
export const deleteMomentPhoto = async (photoId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/photos/${photoId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? '',
            imagePath: result.imagePath ?? result.IMAGEPATH ?? ''
        };
    } catch (error) {
        console.error('Error deleting moment photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Set a photo as the main (cover) photo
 * @param {number} momentId - Moment ID
 * @param {number} photoId - Photo ID to set as main
 * @returns {Promise<Object>} Result with success status
 */
export const setMainPhoto = async (momentId, photoId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/photos/${photoId}/main`, {
            method: 'PUT',
            headers: enTetesEcriture(false)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error setting main photo:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// SKILLS
// ============================================

/**
 * Get skills linked to a moment
 * @param {number} momentId - Moment ID
 * @returns {Promise<Array>} List of skills
 */
export const getMomentSkills = async (momentId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/skills`);
        if (!response.ok) throw new Error('Failed to fetch skills');

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        return success ? data : [];
    } catch (error) {
        console.error('Error fetching moment skills:', error);
        return [];
    }
};

/**
 * Add a skill to a moment
 * @param {number} momentId - Moment ID
 * @param {number} skillId - Skill ID
 * @returns {Promise<Object>} Result with success status
 */
export const addMomentSkill = async (momentId, skillId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/skills`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ skillId })
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            id: result.id ?? result.ID ?? 0,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding moment skill:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Remove a skill from a moment
 * @param {number} momentId - Moment ID
 * @param {number} skillId - Skill ID
 * @returns {Promise<Object>} Result with success status
 */
export const removeMomentSkill = async (momentId, skillId) => {
    try {
        const response = await fetch(`${API_BASE}/moments/${momentId}/skills/${skillId}`, {
            method: 'DELETE',
            headers: enTetesEcriture(false)
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error removing moment skill:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// REORDERING
// ============================================

/**
 * Reorder moments
 * @param {number} peopleId - User ID
 * @param {Array<number>} orderedIds - Array of moment IDs in desired order
 * @returns {Promise<Object>} Result with success status
 */
export const reorderMoments = async (peopleId, orderedIds) => {
    try {
        const response = await fetch(`${API_BASE}/moments/reorder`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleId, orderedIds })
        });

        const result = await response.json();
        return {
            success: result.success ?? result.SUCCESS ?? false,
            message: result.message ?? result.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error reordering moments:', error);
        return { success: false, message: error.message };
    }
};

// ============================================
// HELPERS
// ============================================

/**
 * Get the full URL for a moment photo
 * @param {string} imagePath - Relative image path
 * @returns {string} Full URL
 */
export const getMomentPhotoUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    // imagePath already contains the full relative path (e.g. peoples/{uuid}/moments/file.jpg)
    return `${FILES_BASE_URL}/${imagePath}`;
};

/**
 * Get the cover image URL for a moment
 * @param {Object} moment - Moment object
 * @returns {string} Cover image URL or empty string
 */
export const getMomentCoverUrl = (moment) => {
    if (!moment) return '';

    // Use coverImage from query if available
    const coverImage = moment.coverImage ?? moment.coverimage ?? moment.COVERIMAGE;
    if (coverImage) {
        return getMomentPhotoUrl(coverImage);
    }

    // Fallback to first photo
    const photos = moment.photos ?? moment.PHOTOS ?? [];
    if (photos.length > 0) {
        const firstPhoto = photos[0];
        const imagePath = firstPhoto.imagePath ?? firstPhoto.imagepath ?? firstPhoto.IMAGEPATH;
        return getMomentPhotoUrl(imagePath);
    }

    return '';
};
